import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, MessageSquareMore } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '41782662662';
const WHATSAPP_DEFAULT_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Bonjour, je souhaite obtenir des informations sur CarMaintenance."
)}`;

const INITIAL_MESSAGE = {
  role: 'assistant',
  content:
    "Bonjour, je suis l'assistant CarMaintenance. Je peux vous renseigner sur nos abonnements, conditions générales et notre service. Comment puis-je vous aider aujourd'hui ?",
  escalate: false,
};

const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.11 17.21c-.27-.13-1.6-.79-1.84-.88-.25-.09-.43-.13-.61.13-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.13-1.14-.42-2.17-1.34-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.41.12-.55.12-.12.27-.32.4-.48.13-.16.18-.27.27-.45.09-.18.04-.34-.02-.48-.07-.13-.61-1.47-.83-2.01-.22-.53-.45-.46-.61-.47-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.25.27-.96.94-.96 2.29 0 1.35.98 2.65 1.12 2.83.14.18 1.94 2.96 4.7 4.14.66.28 1.17.45 1.57.58.66.21 1.26.18 1.74.11.53-.08 1.6-.65 1.82-1.28.23-.63.23-1.17.16-1.28-.07-.11-.25-.18-.52-.31zM16 4C9.37 4 4 9.37 4 16c0 2.04.51 3.96 1.42 5.65L4 28l6.55-1.39A11.93 11.93 0 0 0 16 28c6.63 0 12-5.37 12-12S22.63 4 16 4z" />
  </svg>
);

const ChatWidget = () => {
  const { pathname } = useLocation();
  const hideOnAdmin = pathname.startsWith('/admin');
  const hideOnMeyrin = pathname.startsWith('/garage-meyrin');

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState('');
  const [live, setLive] = useState(''); // aria-live announcement
  const listRef = useRef(null);

  // iOS keyboard handling: visualViewport.height reflects the area NOT covered
  // by the on-screen keyboard, .offsetTop reflects how much Safari has translated
  // the page up to keep the focused input visible. We also sync width + offsetLeft
  // so the panel stays anchored to the visible area even when Safari zooms in on
  // the focused input (which would otherwise push the send button off-screen).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const update = () => {
      const vv = window.visualViewport;
      document.documentElement.style.setProperty('--chat-vvh', `${vv.height}px`);
      document.documentElement.style.setProperty('--chat-vvy', `${vv.offsetTop}px`);
      document.documentElement.style.setProperty('--chat-vvw', `${vv.width}px`);
      document.documentElement.style.setProperty('--chat-vvx', `${vv.offsetLeft}px`);
    };
    update();
    window.visualViewport.addEventListener('resize', update);
    window.visualViewport.addEventListener('scroll', update);
    return () => {
      window.visualViewport.removeEventListener('resize', update);
      window.visualViewport.removeEventListener('scroll', update);
    };
  }, []);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  useEffect(() => {
    if (open) {
      // Focus after the open animation
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  if (hideOnAdmin || hideOnMeyrin) return null;

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    setError('');
    const userMsg = { role: 'user', content: trimmed };
    const newHistory = [...messages, userMsg];
    setMessages(newHistory);
    setInput('');
    setLoading(true);

    try {
      const history = newHistory
        .filter((m) => m !== INITIAL_MESSAGE)
        .map(({ role, content }) => ({ role, content }));
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed, history, session_id: sessionId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Erreur ${res.status}`);
      }
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.reply, escalate: data.escalate, whatsappUrl: data.whatsapp_url },
      ]);
      setLive('Nouveau message du bot reçu.');
    } catch (e) {
      setError(e.message || "Erreur lors de l'envoi");
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "Désolé, je rencontre un problème technique. Vous pouvez nous joindre directement sur WhatsApp.",
          escalate: true,
          whatsappUrl: WHATSAPP_DEFAULT_URL,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating button (hidden when open on mobile) */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fermer la conversation' : 'Ouvrir la conversation'}
        aria-expanded={open}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#52a675] hover:bg-[#3d8a5c] text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center ${
          open ? 'scale-90 sm:scale-100' : 'hover:scale-105'
        }`}
        data-testid="chat-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquareMore className="w-6 h-6" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-50 bg-white shadow-2xl border border-gray-200 overflow-hidden flex flex-col
                     rounded-none
                     sm:left-auto sm:!left-auto sm:right-6 sm:bottom-24 sm:w-[360px] sm:h-[540px] sm:rounded-2xl sm:!top-auto"
          style={{
            top: 'var(--chat-vvy, 0px)',
            left: 'var(--chat-vvx, 0px)',
            width: 'var(--chat-vvw, 100%)',
            height: 'var(--chat-vvh, 100dvh)',
          }}
          role="dialog"
          aria-modal="false"
          aria-label="Assistant CarMaintenance"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-[#52a675] text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-semibold">Assistant CarMaintenance</p>
                <p className="text-[11px] text-white/85">Réponses instantanées</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1.5 hover:bg-white/15 rounded-md transition-colors"
              aria-label="Fermer la conversation"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Persistent WhatsApp link */}
          <a
            href={WHATSAPP_DEFAULT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#25D366]/10 hover:bg-[#25D366]/15 text-[#1b6e3d] border-b border-[#25D366]/20 px-4 py-2 text-xs flex items-center justify-between transition-colors shrink-0"
            data-testid="chat-whatsapp-header"
          >
            <span className="flex items-center gap-2 font-medium">
              <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" /> Contactez-nous sur WhatsApp
            </span>
            <span className="text-[#25D366]" aria-hidden="true">→</span>
          </a>

          {/* Messages */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5 bg-gray-50"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => (
              <MessageBubble key={i} message={m} />
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-[#52a675]/10 border border-[#52a675]/20 rounded-2xl rounded-tl-sm px-3 py-2.5 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-[#52a675] rounded-full animate-pulse [animation-delay:0ms]"></span>
                  <span className="w-1.5 h-1.5 bg-[#52a675] rounded-full animate-pulse [animation-delay:120ms]"></span>
                  <span className="w-1.5 h-1.5 bg-[#52a675] rounded-full animate-pulse [animation-delay:240ms]"></span>
                </div>
              </div>
            )}
            {error && (
              <p className="text-xs text-red-600 px-2">{error}</p>
            )}
          </div>

          {/* Input */}
          <div
            className="border-t border-gray-200 p-2 bg-white shrink-0"
            style={{ paddingRight: 'max(0.5rem, env(safe-area-inset-right))', paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
          >
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Écrivez votre message…"
                rows={1}
                maxLength={1000}
                className="flex-1 min-w-0 resize-none rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#52a675]/40 focus:border-[#52a675] max-h-24"
                style={{ fontSize: '16px' }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="sentences"
                spellCheck={false}
                name="chat-message"
                enterKeyHint="send"
                inputMode="text"
                disabled={loading}
                data-testid="chat-input"
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-[#52a675] hover:bg-[#3d8a5c] disabled:bg-gray-300 text-white rounded-xl w-10 h-10 flex items-center justify-center transition-colors shrink-0"
                aria-label="Envoyer le message"
                data-testid="chat-send"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1 px-1">
              Pour un cas personnel ou urgent, privilégiez WhatsApp.
            </p>
          </div>

          {/* SR live region */}
          <span className="sr-only" aria-live="polite">{live}</span>
        </div>
      )}
    </>
  );
};

/**
 * Single chat bubble — preserves line breaks and renders inline links.
 * For escalation, appends a "Continuer sur WhatsApp" CTA.
 */
const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  const base = isUser
    ? 'bg-gray-200 text-gray-900 rounded-2xl rounded-br-sm'
    : 'bg-[#52a675]/10 border border-[#52a675]/15 text-gray-800 rounded-2xl rounded-tl-sm';

  // Render minimal markdown: bold **xxx** and newlines
  const formatted = renderInline(message.content || '');

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${base}`}>
        <p className="whitespace-pre-wrap">{formatted}</p>
        {message.escalate && message.whatsappUrl && (
          <a
            href={message.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#20BA5A] text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
            data-testid="chat-whatsapp-cta"
          >
            <WhatsAppIcon className="w-3.5 h-3.5" />
            Continuer sur WhatsApp
          </a>
        )}
      </div>
    </div>
  );
};

// Inline bold rendering (**word** → <strong>word</strong>)
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default ChatWidget;
